<template>
  <div class="flex items-center gap-1.5 flex-wrap">
    <HassSelector
      class="text-sm flex-1 min-w-[9rem]"
      :modelValue="leftName"
      :selector="runtimeVariableSelector"
      @update:modelValue="onLeftChange" />

    <HassSelector
      v-if="attributeMode"
      class="text-sm flex-1 min-w-[8rem]"
      :modelValue="condition.left.attribute"
      :settings="settings"
      placeholder="attribute"
      :selector="attributeSelector"
      @update:modelValue="onAttributeChange" />

    <form-select class="text-sm w-16 flex-shrink-0" v-model="condition.operator">
      <option v-for="op in OPERATORS" :key="op" :value="op">{{ op }}</option>
    </form-select>

    <PropertyEditorFactory
      class="flex-1 min-w-[7rem]"
      :editorMeta="rightEditorMeta"
      :modelValue="condition.right?.value ?? ''"
      @update:modelValue="onRightChange" />

    <button
      class="ha-builder-danger-button"
      title="Remove condition" @click="$emit('remove')">
      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
</template>

<script setup>
/**
 * ConditionEditor.vue
 *
 * Renders a single AutomationCondition using semantic labels only — the left
 * operand is always one of the widget's declared runtimeVariables (never a
 * raw variable name like "val" or "text"); the right operand's editor is
 * chosen by that variable's type via PropertyEditorFactory. Given an
 * `entityId` (the flow graph's condition node), the left operand can also be
 * that entity's state or one of its attributes; the entity itself is
 * resolved at export, so the condition keeps an empty entityId.
 */
import { computed, ref } from 'vue';
import { AutomationValue } from '../../../automation/ast/AutomationValue.js';
import PropertyEditorFactory from './PropertyEditorFactory.vue';
import HassSelector from '../HassSelector.vue';

const props = defineProps({
  condition:        { type: Object, required: true }, // AutomationCondition
  runtimeVariables: { type: Array,  default: () => [] }, // [{ name, type, label, optionsFrom }]
  node:             { type: Object, default: null }, // widget instance — read for optionsFrom lookups
  entityId:         { type: String, default: null }, // entity operands offered when not null ('' = unresolved)
  settings:         { type: Object, default: null }, // HA metadata for the attribute selector
});
defineEmits(['remove']);

const OPERATORS = ['==', '!=', '>', '<', '>=', '<='];

const ENTITY_STATE = '@entity-state';
const ENTITY_ATTRIBUTE = '@entity-attribute';

const isEntityLeft = computed(() => props.condition.left?.kind === 'entity');
// "Entity attribute" stays selected while no attribute is picked yet.
const attributeMode = ref(isEntityLeft.value && Boolean(props.condition.left.attribute));

const leftName = computed(() => {
  if (!isEntityLeft.value) return props.condition.left?.name ?? '';
  return attributeMode.value ? ENTITY_ATTRIBUTE : ENTITY_STATE;
});

const leftVariable = computed(() =>
  props.runtimeVariables.find(v => v.name === leftName.value) ?? null
);

const entityOptions = computed(() => {
  if (props.entityId === null) return [];
  const suffix = props.entityId ? ` (${props.entityId})` : '';
  return [
    { value: ENTITY_STATE, label: `Entity state${suffix}` },
    { value: ENTITY_ATTRIBUTE, label: `Entity attribute${suffix}` },
  ];
});

const runtimeVariableSelector = computed(() => ({
  kind: 'select',
  options: [
    ...props.runtimeVariables.map(variable => ({ value: variable.name, label: variable.label })),
    ...entityOptions.value,
  ],
  preserveUnknown: true,
}));

const attributeSelector = computed(() => ({
  kind: 'attribute',
  entityId: props.entityId ?? '',
  preserveUnknown: true,
}));

/** Widget-configured options (e.g. dropdown/roller's `options` list) for a variable declaring optionsFrom. */
function widgetOptions(variable) {
  const raw = props.node?.[variable.optionsFrom];
  if (typeof raw !== 'string') return [];
  return raw.split('\n').map(s => s.trim()).filter(Boolean);
}

const rightEditorMeta = computed(() => {
  const variable = leftVariable.value;
  if (variable?.optionsFrom) return { editor: 'enum', values: widgetOptions(variable) };

  switch (variable?.type) {
    case 'int':   return { editor: 'number' };
    case 'bool':  return { editor: 'bool' };
    case 'color': return { editor: 'color' };
    default:      return { editor: 'text' };
  }
});

function onLeftChange(name) {
  attributeMode.value = name === ENTITY_ATTRIBUTE;
  props.condition.left = name === ENTITY_STATE || name === ENTITY_ATTRIBUTE
    ? AutomationValue.entity()
    : AutomationValue.reference(name);
}

function onAttributeChange(attribute) {
  props.condition.left = AutomationValue.entity('', attribute ?? '');
}

function onRightChange(value) {
  props.condition.right = AutomationValue.literal(value);
}
</script>
